import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Notification } from '../types/notifications';
import { notificationsService } from '../services/notificationsService';
import { useAuth } from './AuthContext';

interface NotificationsContextType {
  notifications: Notification[];
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  addTestNotification: () => void; // For testing purposes
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user } = useAuth();

  // Load notifications from local storage on mount and when user changes
  useEffect(() => {
    if (user) {
      const storedNotifications = notificationsService.getNotifications();
      setNotifications(storedNotifications);
    } else {
      // Clear notifications when user logs out
      setNotifications([]);
    }
  }, [user]);

  const removeNotification = (id: string) => {
    notificationsService.removeNotification(id);
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearNotifications = () => {
    notificationsService.clearNotifications();
    setNotifications([]);
  };

  // For testing purposes only
  const addTestNotification = () => {
    const newNotification = notificationsService.createGoodThingNotification('Test User');
    setNotifications(prev => [newNotification, ...prev]);
  };

  const value = {
    notifications,
    removeNotification,
    clearNotifications,
    addTestNotification
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
